// pages/RecommendationsPage.jsx
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNotification } from "../context/NotificationContext";
import {
  StatusBadge,
  SourceBadge,
  ConfidenceBadge,
  UpliftBadge,
  FallbackBadge,
} from "../components/Badges";
import ActionButtons from "../components/ActionButtons";
import DetailPanel from "../components/DetailPanel";
import { ConfirmModal, PageHeader } from "../components/UI";
import * as api from "../services/dynamicPriceApi";

const companyId = localStorage.getItem("companyid") || 1;

const SOURCE_ORDER = ["PMS", "BE", "OTA"];
const CHANNEL_OPTIONS = [
  { key: "ALL", label: "All Channels" },
  { key: "PMS", label: "PMS" },
  { key: "BE", label: "Booking Engine" },
  { key: "OTA", label: "OTA" },
];
const STATUS_OPTIONS = ["ALL", "PENDING", "APPROVED", "MODIFIED", "REJECTED"];

const STATUS_CFG = {
  ALL: { color: "#475569", bg: "#f8fafc", border: "#e2e8f0" },
  PENDING: { color: "#c2410c", bg: "#fff7ed", border: "#fdba74" },
  APPROVED: { color: "#166534", bg: "#f0fdf4", border: "#86efac" },
  MODIFIED: { color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" },
  REJECTED: { color: "#be123c", bg: "#fff1f2", border: "#fecdd3" },
};

const SOURCE_CFG = {
  ALL: { color: "#475569", bg: "#f8fafc", border: "#e2e8f0" },
  PMS: { color: "#6d28d9", bg: "#f5f3ff", border: "#c4b5fd" },
  BE: { color: "#0369a1", bg: "#e0f2fe", border: "#7dd3fc" },
  OTA: { color: "#be185d", bg: "#fdf2f8", border: "#f9a8d4" },
};

// ---------- Helpers ----------
function num(v, d = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
}

function fmt(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `₹${Math.round(n).toLocaleString("en-IN")}`;
}

function pctDelta(base, ai) {
  const b = num(base);
  if (!b) return 0;
  return ((num(ai) - b) / b) * 100;
}

function upliftAmount(base, ai) {
  return num(ai) - num(base);
}

function buildGroupKey(rec) {
  const roomKey = rec.room_type_id ?? rec.type_id ?? rec.type_name ?? "UNKNOWN";
  return `${rec.checkin_date}__${roomKey}`;
}

function sortSources(rows) {
  return [...rows].sort((a, b) => {
    const ia = SOURCE_ORDER.indexOf(a.source_channel);
    const ib = SOURCE_ORDER.indexOf(b.source_channel);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });
}

function normalizeSourceChannel(v) {
  const s = String(v || "").trim().toUpperCase();
  if (s === "BOOKING_ENGINE" || s === "DIRECT" || s === "WEB") return "BE";
  if (s === "PMS") return "PMS";
  if (s === "OTA") return "OTA";
  return s || "PMS";
}

function normalizeStatus(v) {
  const s = String(v || "PENDING").trim().toUpperCase();
  if (["PENDING", "APPROVED", "MODIFIED", "REJECTED"].includes(s)) return s;
  return "PENDING";
}

function normalizeDate(v) {
  if (!v) return "";
  // Accept ISO datetime or YYYY-MM-DD
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function normalizeRecommendationRow(raw, idx = 0) {
  // Defensive mapping for varying backend keys
  const id = raw.id ?? raw.recommendation_id ?? raw.rec_id ?? `tmp-${idx}`;
  const source_channel = normalizeSourceChannel(
    raw.source_channel ?? raw.source ?? raw.channel
  );
  const status = normalizeStatus(raw.status);

  const baseRate =
    raw.base_rate_single ??
    raw.base_rate ??
    raw.current_rate ??
    raw.base_price ??
    0;

  const recommended =
    raw.recommended_price ??
    raw.ai_price ??
    raw.suggested_price ??
    raw.proposed_price ??
    0;

  const finalPrice =
    raw.final_price ?? raw.approved_price ?? raw.live_price ?? null;

  const competitorAvg =
    raw.competitor_avg_price ??
    raw.competitor_average_price ??
    raw.market_avg_price ??
    raw.competitor_price ??
    0;

  const confidence =
    raw.confidence_score ??
    raw.confidence ??
    raw.ai_confidence ??
    0;

  const roomTypeId =
    raw.room_type_id ?? raw.type_id ?? raw.internal_room_type_id ?? null;

  const typeName =
    raw.type_name ??
    raw.room_type_name ??
    raw.room_type ??
    raw.room_name ??
    "Room Type";

  const checkin =
    normalizeDate(raw.checkin_date ?? raw.check_in_date ?? raw.date);

  const reasoning =
    raw.reasoning ??
    raw.ai_reasoning ??
    raw.explanation ??
    raw.model_reasoning ??
    "";

  return {
    ...raw,
    id,
    source_channel,
    status,
    base_rate_single: num(baseRate),
    recommended_price: num(recommended),
    final_price: finalPrice == null ? null : num(finalPrice),
    competitor_avg_price: num(competitorAvg),
    confidence_score: num(confidence),
    room_type_id: roomTypeId,
    type_id: raw.type_id ?? roomTypeId,
    type_name: typeName,
    checkin_date: checkin,
    local_events: raw.local_events ?? raw.event_name ?? raw.events ?? "No major events",
    is_holiday: !!(raw.is_holiday ?? raw.holiday_flag),
    rate_fallback: !!(raw.rate_fallback ?? raw.used_fallback_rate),
    reasoning,
  };
}

function extractRowsFromResponse(res) {
  const d = res?.data;
  const rows =
    d?.data?.rows ||
    d?.data?.recommendations ||
    d?.recommendations ||
    d?.rows ||
    d?.data ||
    [];
  return Array.isArray(rows) ? rows : [];
}

function reasoningPreview(text) {
  const s = String(text || "").trim();
  if (!s) return "No reasoning";
  return s.length > 70 ? `${s.slice(0, 70)}…` : s;
}

// Small inline tooltip chip
function ReasoningCell({ reasoning }) {
  const [open, setOpen] = useState(false);
  const hasReasoning = !!String(reasoning || "").trim();

  return (
    <div style={{ position: "relative", minWidth: 0 }}>
      <button
        type="button"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        style={{
          border: `1px solid ${hasReasoning ? "#bfdbfe" : "#e2e8f0"}`,
          background: hasReasoning ? "#eff6ff" : "#f8fafc",
          color: hasReasoning ? "#1d4ed8" : "#94a3b8",
          borderRadius: 999,
          padding: "4px 8px",
          fontSize: 11,
          fontWeight: 800,
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          cursor: hasReasoning ? "help" : "default",
          maxWidth: "100%",
        }}
        title={hasReasoning ? reasoning : "No AI reasoning available"}
      >
        <span style={{ fontSize: 12 }}>🧠</span>
        <span
          style={{
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "inline-block",
            maxWidth: 110,
          }}
        >
          {reasoningPreview(reasoning)}
        </span>
      </button>

      {open && hasReasoning && (
        <div
          style={{
            position: "absolute",
            zIndex: 50,
            top: "110%",
            left: 0,
            width: 320,
            maxWidth: "42vw",
            background: "#0f172a",
            color: "#e2e8f0",
            borderRadius: 10,
            padding: "10px 12px",
            boxShadow: "0 10px 30px rgba(2,6,23,0.30)",
            border: "1px solid rgba(148,163,184,0.25)",
            fontSize: 12,
            lineHeight: 1.5,
            fontWeight: 500,
            whiteSpace: "normal",
          }}
        >
          <div
            style={{
              fontSize: 10,
              textTransform: "uppercase",
              letterSpacing: 0.8,
              color: "#93c5fd",
              fontWeight: 800,
              marginBottom: 6,
            }}
          >
            AI Reasoning
          </div>
          {reasoning}
        </div>
      )}
    </div>
  );
}

export default function RecommendationsPage() {
  const { notify } = useNotification();

  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [statusFilter, setStF] = useState("ALL");
  const [srcFilter, setSrcF] = useState("ALL");
  const [dateFilter, setDtF] = useState("ALL");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("DATE_ASC"); // DATE_ASC | UPLIFT_DESC | CONFIDENCE_ASC
  const [onlyAttention, setOnlyAttention] = useState(false);

  const [selected, setSel] = useState(null); // source-level detail
  const [confirmAll, setCA] = useState(false);

  const [selectedGroups, setSelectedGroups] = useState(() => new Set());
  const [guardrailModal, setGuardrailModal] = useState({
    open: false,
    mode: null, // BULK_SELECTED | BULK_ALL_PENDING
    candidateIds: [],
    summary: null,
  });

  const loadRecommendations = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    else setRefreshing(true);

    try {
      const res = await api.getRecommendations(companyId);
      const rows = extractRowsFromResponse(res).map(normalizeRecommendationRow);
      setRecs(rows);
    } catch (err) {
      notify("error", err?.response?.data?.message || "Failed to load recommendations.");
    } finally {
      if (showSpinner) setLoading(false);
      setRefreshing(false);
    }
  }, [notify]);

  useEffect(() => {
    loadRecommendations(true);
  }, [loadRecommendations]);

  const mutate = (id, patch) => {
    setRecs((r) => r.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  };

  const mutateMany = (ids, patcher) => {
    const idSet = new Set(ids);
    setRecs((rows) =>
      rows.map((r) => (idSet.has(r.id) ? { ...r, ...patcher(r) } : r))
    );
  };

  const handleApprove = async (id) => {
    try {
      const rec = recs.find((r) => r.id === id);
      await api.approveRecommendation(companyId, id);

      mutate(id, {
        status: "APPROVED",
        final_price: rec?.recommended_price,
      });

      if (selected?.id === id) {
        setSel((s) => ({
          ...s,
          status: "APPROVED",
          final_price: rec?.recommended_price,
        }));
      }

      notify("success", "Recommendation approved — price is now live.");
    } catch (err) {
      notify("error", err?.response?.data?.message || "Failed to approve. Please try again.");
    }
  };

  const handleReject = async (id) => {
    try {
      await api.rejectRecommendation(companyId, id);
      mutate(id, { status: "REJECTED" });

      if (selected?.id === id) {
        setSel((s) => ({ ...s, status: "REJECTED" }));
      }

      notify("error", "Recommendation rejected — base rate retained.");
    } catch (err) {
      notify("error", err?.response?.data?.message || "Failed to reject. Please try again.");
    }
  };

  const handleModify = async (id, price) => {
    try {
      const p = Number(price);
      if (!Number.isFinite(p) || p <= 0) {
        notify("error", "Enter a valid price.");
        return;
      }

      await api.modifyRecommendation(companyId, id, p);
      mutate(id, { status: "MODIFIED", final_price: p });

      if (selected?.id === id) {
        setSel((s) => ({ ...s, status: "MODIFIED", final_price: p }));
      }

      notify("success", `Price set to ${fmt(p)} — recommendation modified.`);
    } catch (err) {
      notify("error", err?.response?.data?.message || "Failed to modify price. Please try again.");
    }
  };

  const allDates = useMemo(
    () => [...new Set(recs.map((r) => r.checkin_date).filter(Boolean))].sort(),
    [recs]
  );

  const pending = useMemo(
    () => recs.filter((r) => r.status === "PENDING").length,
    [recs]
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();

    return recs.filter((r) => {
      const matchesBase =
        (statusFilter === "ALL" || r.status === statusFilter) &&
        (srcFilter === "ALL" || r.source_channel === srcFilter) &&
        (dateFilter === "ALL" || r.checkin_date === dateFilter);

      if (!matchesBase) return false;

      const textBlob = [
        r.type_name,
        r.source_channel,
        r.checkin_date,
        r.local_events,
        r.status,
        r.reasoning,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      if (q && !textBlob.includes(q)) return false;

      if (!onlyAttention) return true;

      const lowConfidence = num(r.confidence_score) < 0.7;
      const hasEvent = !!(r.local_events && r.local_events !== "No major events");

      return (
        r.status === "PENDING" ||
        lowConfidence ||
        !!r.rate_fallback ||
        !!r.is_holiday ||
        hasEvent
      );
    });
  }, [recs, statusFilter, srcFilter, dateFilter, search, onlyAttention]);

  const grouped = useMemo(() => {
    const map = new Map();

    for (const rec of filteredRows) {
      const key = buildGroupKey(rec);
      if (!map.has(key)) {
        map.set(key, {
          key,
          checkin_date: rec.checkin_date,
          room_type_id: rec.room_type_id ?? rec.type_id ?? null,
          type_name: rec.type_name,
          is_holiday: !!rec.is_holiday,
          local_events: rec.local_events,
          rows: [],
        });
      }
      const g = map.get(key);
      g.rows.push(rec);

      g.is_holiday = g.is_holiday || !!rec.is_holiday;
      if (
        rec.local_events &&
        rec.local_events !== "No major events" &&
        (!g.local_events || g.local_events === "No major events")
      ) {
        g.local_events = rec.local_events;
      }
    }

    const groups = Array.from(map.values()).map((g) => {
      const rows = sortSources(g.rows);

      const pendingRows = rows.filter((r) => r.status === "PENDING");
      const avgConfidence =
        rows.length > 0
          ? rows.reduce((s, r) => s + num(r.confidence_score), 0) / rows.length
          : 0;

      const lowConfidenceCount = rows.filter((r) => num(r.confidence_score) < 0.7).length;
      const fallbackCount = rows.filter((r) => !!r.rate_fallback).length;

      const upliftPcts = rows.map((r) => pctDelta(r.base_rate_single, r.recommended_price));
      const aiPrices = rows.map((r) => num(r.recommended_price));

      const groupPotentialAmount = pendingRows.reduce(
        (sum, r) => sum + Math.max(0, upliftAmount(r.base_rate_single, r.recommended_price)),
        0
      );

      return {
        ...g,
        rows,
        pendingCount: pendingRows.length,
        approvedCount: rows.filter((r) => r.status === "APPROVED").length,
        modifiedCount: rows.filter((r) => r.status === "MODIFIED").length,
        rejectedCount: rows.filter((r) => r.status === "REJECTED").length,
        avgConfidence,
        lowConfidenceCount,
        fallbackCount,
        groupPotentialAmount,
        minAiPrice: aiPrices.length ? Math.min(...aiPrices) : 0,
        maxAiPrice: aiPrices.length ? Math.max(...aiPrices) : 0,
        maxAbsUpliftPct: upliftPcts.length
          ? Math.max(...upliftPcts.map((v) => Math.abs(v)))
          : 0,
        hasEvent: !!(g.local_events && g.local_events !== "No major events"),
      };
    });

    groups.sort((a, b) => {
      if (sortBy === "UPLIFT_DESC") return b.maxAbsUpliftPct - a.maxAbsUpliftPct;
      if (sortBy === "CONFIDENCE_ASC") return a.avgConfidence - b.avgConfidence;

      if (a.checkin_date !== b.checkin_date) return String(a.checkin_date).localeCompare(String(b.checkin_date));
      if (a.pendingCount !== b.pendingCount) return b.pendingCount - a.pendingCount;
      return String(a.type_name || "").localeCompare(String(b.type_name || ""));
    });

    return groups;
  }, [filteredRows, sortBy]);

  const groupedByDate = useMemo(() => {
    return grouped.reduce((acc, g) => {
      if (!acc[g.checkin_date]) acc[g.checkin_date] = [];
      acc[g.checkin_date].push(g);
      return acc;
    }, {});
  }, [grouped]);

  const sortedDates = useMemo(
    () => Object.keys(groupedByDate).sort((a, b) => a.localeCompare(b)),
    [groupedByDate]
  );

  const visibleStats = useMemo(() => {
    const visibleRows = grouped.flatMap((g) => g.rows);
    const visiblePendingRows = visibleRows.filter((r) => r.status === "PENDING");
    const lowConfidenceCount = visibleRows.filter((r) => num(r.confidence_score) < 0.7).length;
    const fallbackCount = visibleRows.filter((r) => !!r.rate_fallback).length;
    const potentialUpliftAmount = visiblePendingRows.reduce(
      (sum, r) => sum + Math.max(0, upliftAmount(r.base_rate_single, r.recommended_price)),
      0
    );

    return {
      groupCount: grouped.length,
      sourceRowCount: visibleRows.length,
      pendingRows: visiblePendingRows.length,
      lowConfidenceCount,
      fallbackCount,
      potentialUpliftAmount,
    };
  }, [grouped]);

  const visibleGroupKeys = useMemo(() => grouped.map((g) => g.key), [grouped]);

  const isAllVisibleSelected =
    visibleGroupKeys.length > 0 &&
    visibleGroupKeys.every((k) => selectedGroups.has(k));

  const toggleGroupSelection = (groupKey) => {
    setSelectedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  };

  const toggleSelectAllVisibleGroups = () => {
    setSelectedGroups((prev) => {
      const next = new Set(prev);
      if (isAllVisibleSelected) {
        visibleGroupKeys.forEach((k) => next.delete(k));
      } else {
        visibleGroupKeys.forEach((k) => next.add(k));
      }
      return next;
    });
  };

  const resetFilters = () => {
    setStF("ALL");
    setSrcF("ALL");
    setDtF("ALL");
    setSearch("");
    setSortBy("DATE_ASC");
    setOnlyAttention(false);
  };

  const groupPendingIds = (group) =>
    group.rows.filter((r) => r.status === "PENDING").map((r) => r.id);

  const handleApproveGroup = async (group) => {
    const ids = groupPendingIds(group);
    if (!ids.length) {
      notify("error", "No pending source recommendations in this room type.");
      return;
    }

    try {
      // Sequential or parallel individual approvals (backend may not expose bulk endpoint)
      await Promise.all(ids.map((id) => api.approveRecommendation(companyId, id)));

      mutateMany(ids, (r) => ({
        status: "APPROVED",
        final_price: r.recommended_price,
      }));

      notify("success", `${group.type_name}: approved ${ids.length} pending source recommendation(s).`);
    } catch (err) {
      notify("error", err?.response?.data?.message || "Failed to approve group.");
    }
  };

  const handleRejectGroup = async (group) => {
    const ids = groupPendingIds(group);
    if (!ids.length) {
      notify("error", "No pending source recommendations in this room type.");
      return;
    }

    try {
      await Promise.all(ids.map((id) => api.rejectRecommendation(companyId, id)));
      mutateMany(ids, () => ({ status: "REJECTED" }));
      notify("error", `${group.type_name}: rejected ${ids.length} pending source recommendation(s).`);
    } catch (err) {
      notify("error", err?.response?.data?.message || "Failed to reject group.");
    }
  };

  const buildGuardrailSummary = (candidateRows) => {
    const lowConfidence = candidateRows.filter((r) => num(r.confidence_score) < 0.7);
    const highUplift = candidateRows.filter((r) => pctDelta(r.base_rate_single, r.recommended_price) > 20);
    const compTooHigh = candidateRows.filter((r) => {
      const comp = num(r.competitor_avg_price);
      if (!comp) return false;
      return num(r.recommended_price) > comp * 1.15;
    });
    const fallback = candidateRows.filter((r) => !!r.rate_fallback);

    return {
      total: candidateRows.length,
      lowConfidence: lowConfidence.length,
      highUplift: highUplift.length,
      compTooHigh: compTooHigh.length,
      fallback: fallback.length,
    };
  };

  const openBulkApproveGuardrails = (mode) => {
    let candidateRows = [];

    if (mode === "BULK_ALL_PENDING") {
      candidateRows = recs.filter((r) => r.status === "PENDING");
    } else {
      const selected = grouped.filter((g) => selectedGroups.has(g.key));
      candidateRows = selected.flatMap((g) => g.rows.filter((r) => r.status === "PENDING"));
    }

    if (!candidateRows.length) {
      notify("error", "No pending recommendations to approve in the selected scope.");
      return;
    }

    setGuardrailModal({
      open: true,
      mode,
      candidateIds: candidateRows.map((r) => r.id),
      summary: buildGuardrailSummary(candidateRows),
    });
  };

  const runGuardedBulkApprove = async () => {
    const ids = guardrailModal.candidateIds || [];
    if (!ids.length) {
      setGuardrailModal({ open: false, mode: null, candidateIds: [], summary: null });
      return;
    }

    try {
      // If "approve all pending" endpoint exists and scope is global, use it; otherwise fallback to per-id
      if (guardrailModal.mode === "BULK_ALL_PENDING" && typeof api.approveAllRecommendations === "function") {
        await api.approveAllRecommendations(companyId);
      } else {
        await Promise.all(ids.map((id) => api.approveRecommendation(companyId, id)));
      }

      mutateMany(ids, (r) => ({
        status: "APPROVED",
        final_price: r.recommended_price,
      }));

      notify("success", `Approved ${ids.length} recommendation(s).`);
      setGuardrailModal({ open: false, mode: null, candidateIds: [], summary: null });
      setCA(false);
    } catch (err) {
      notify("error", err?.response?.data?.message || "Bulk approve failed. Please try again.");
    }
  };

  const filterBtnStyle = (active, cfg) => ({
    padding: "6px 12px",
    borderRadius: 8,
    border: `1.5px solid ${active ? cfg?.border || "#e2e8f0" : "#e2e8f0"}`,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 700,
    background: active ? cfg?.bg || "#f1f5f9" : "#fff",
    color: active ? cfg?.color || "#334155" : "#64748b",
    fontFamily: "'Outfit', sans-serif",
    transition: "all 0.12s",
    whiteSpace: "nowrap",
  });

  const statCard = (label, value, sub, tone = "default") => {
    const toneMap = {
      default: { bg: "#fff", border: "#e2e8f0", value: "#0f172a", sub: "#64748b" },
      pending: { bg: "#fffaf0", border: "#fed7aa", value: "#9a3412", sub: "#c2410c" },
      uplift: { bg: "#eff6ff", border: "#bfdbfe", value: "#1d4ed8", sub: "#2563eb" },
      risk: { bg: "#fff1f2", border: "#fecdd3", value: "#be123c", sub: "#e11d48" },
    };
    const c = toneMap[tone] || toneMap.default;
    return (
      <div
        style={{
          background: c.bg,
          border: `1px solid ${c.border}`,
          borderRadius: 12,
          padding: "12px 14px",
          minWidth: 180,
          boxShadow: "0 1px 4px rgba(15,23,42,0.04)",
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: "#94a3b8",
            fontWeight: 800,
            letterSpacing: 0.7,
            textTransform: "uppercase",
            marginBottom: 6,
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: 20,
            fontWeight: 800,
            color: c.value,
            letterSpacing: -0.3,
            lineHeight: 1.1,
          }}
        >
          {value}
        </div>
        {sub ? (
          <div style={{ marginTop: 5, fontSize: 12, color: c.sub, fontWeight: 600 }}>
            {sub}
          </div>
        ) : null}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: "24px 28px" }}>
        <div style={{ fontSize: 14, color: "#94a3b8", fontWeight: 600 }}>
          Loading recommendations…
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px 28px" }}>
      <PageHeader
        title="Price Recommendations"
        subtitle="One view per room type/date with per-source pricing decisions (PMS / BE / OTA)"
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <button
            className="dp-btn dp-btn-outline"
            onClick={() => loadRecommendations(false)}
            disabled={refreshing}
          >
            {refreshing ? (
              <>
                <span className="dp-spin">⟳</span> Refreshing...
              </>
            ) : (
              "↻ Refresh"
            )}
          </button>

          {pending > 0 && (
            <button className="dp-btn dp-btn-success" onClick={() => setCA(true)}>
              ✓ Approve All Pending ({pending})
            </button>
          )}

          <button
            type="button"
            className="dp-btn"
            onClick={() => openBulkApproveGuardrails("BULK_SELECTED")}
            disabled={selectedGroups.size === 0}
            style={{
              opacity: selectedGroups.size === 0 ? 0.5 : 1,
              cursor: selectedGroups.size === 0 ? "not-allowed" : "pointer",
            }}
          >
            Approve Selected Groups
          </button>
        </div>
      </PageHeader>

      {/* KPI STRIP */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
          gap: 12,
          marginBottom: 14,
        }}
      >
        {statCard("Visible Room-Type Groups", visibleStats.groupCount, `${visibleStats.sourceRowCount} source rows`)}
        {statCard("Pending Source Decisions", visibleStats.pendingRows, "Across visible groups", "pending")}
        {statCard("Potential Uplift", fmt(visibleStats.potentialUpliftAmount), "Visible pending rows", "uplift")}
        {statCard(
          "Risk / Exceptions",
          `${visibleStats.lowConfidenceCount + visibleStats.fallbackCount}`,
          `${visibleStats.lowConfidenceCount} low confidence · ${visibleStats.fallbackCount} fallback`,
          "risk"
        )}
      </div>

      {/* FILTER BAR */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
          background: "#fff",
          borderRadius: 12,
          padding: "12px 14px",
          border: "1px solid #e2e8f0",
          marginBottom: 16,
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        }}
      >
        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            marginRight: 4,
            color: "#475569",
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          <input type="checkbox" checked={isAllVisibleSelected} onChange={toggleSelectAllVisibleGroups} />
          Select all visible groups
        </label>

        <div style={{ width: 1, height: 24, background: "#e2e8f0" }} />

        <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.8 }}>
          Status
        </span>
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s}
            onClick={() => setStF(s)}
            style={filterBtnStyle(statusFilter === s, STATUS_CFG[s])}
          >
            {s === "ALL" ? "All" : s.charAt(0) + s.slice(1).toLowerCase()}
          </button>
        ))}

        <div style={{ width: 1, height: 24, background: "#e2e8f0" }} />

        <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.8 }}>
          Channel
        </span>
        {CHANNEL_OPTIONS.map((s) => (
          <button
            key={s.key}
            onClick={() => setSrcF(s.key)}
            style={filterBtnStyle(srcFilter === s.key, SOURCE_CFG[s.key])}
          >
            {s.label}
          </button>
        ))}

        <select
          value={dateFilter}
          onChange={(e) => setDtF(e.target.value)}
          style={{
            border: "1.5px solid #e2e8f0",
            borderRadius: 8,
            padding: "7px 34px 7px 12px",
            fontSize: 12,
            fontWeight: 700,
            color: "#475569",
            outline: "none",
            background: "#fff",
            fontFamily: "'Outfit', sans-serif",
            cursor: "pointer",
            minWidth: 130,
          }}
        >
          <option value="ALL">All Dates</option>
          {allDates.map((d) => (
            <option key={d} value={d}>
              {new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
            </option>
          ))}
        </select>

        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search room type / event / date / reasoning..."
          style={{
            border: "1.5px solid #e2e8f0",
            borderRadius: 8,
            padding: "7px 12px",
            fontSize: 12,
            fontWeight: 600,
            color: "#334155",
            outline: "none",
            background: "#fff",
            fontFamily: "'Outfit', sans-serif",
            minWidth: 220,
            flex: "0 1 280px",
          }}
        />

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          style={{
            border: "1.5px solid #e2e8f0",
            borderRadius: 8,
            padding: "7px 34px 7px 12px",
            fontSize: 12,
            fontWeight: 700,
            color: "#475569",
            outline: "none",
            background: "#fff",
            fontFamily: "'Outfit', sans-serif",
            cursor: "pointer",
            minWidth: 170,
          }}
        >
          <option value="DATE_ASC">Sort: Date (Default)</option>
          <option value="UPLIFT_DESC">Sort: Max Uplift %</option>
          <option value="CONFIDENCE_ASC">Sort: Avg Confidence (Low → High)</option>
        </select>

        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            fontSize: 12,
            fontWeight: 700,
            color: "#475569",
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          <input
            type="checkbox"
            checked={onlyAttention}
            onChange={(e) => setOnlyAttention(e.target.checked)}
          />
          Only attention needed
        </label>

        <button
          type="button"
          onClick={resetFilters}
          style={{
            marginLeft: "auto",
            border: "1.5px solid #e2e8f0",
            background: "#fff",
            color: "#64748b",
            borderRadius: 8,
            padding: "7px 12px",
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          Reset
        </button>

        <span style={{ fontSize: 12, color: "#94a3b8", fontWeight: 700 }}>
          {grouped.length} groups
        </span>
      </div>

      {/* MAIN LAYOUT */}
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {sortedDates.map((date) => {
            const dateGroups = groupedByDate[date] || [];
            const hasHoliday = dateGroups.some((g) => g.is_holiday);
            const hasEvent = dateGroups.some((g) => g.hasEvent);
            const datePending = dateGroups.reduce((s, g) => s + g.pendingCount, 0);

            return (
              <div key={date} style={{ marginBottom: 18 }}>
                {/* Date Header */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 10,
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ width: 4, height: 20, background: "#2563eb", borderRadius: 2 }} />
                  <span style={{ fontSize: 14, fontWeight: 800, color: "#0f172a", letterSpacing: -0.2 }}>
                    {new Date(date + "T00:00:00").toLocaleDateString("en-IN", {
                      weekday: "long",
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </span>

                  <span
                    style={{
                      fontSize: 11,
                      background: "#f8fafc",
                      color: "#475569",
                      border: "1px solid #e2e8f0",
                      borderRadius: 999,
                      padding: "3px 8px",
                      fontWeight: 700,
                    }}
                  >
                    {dateGroups.length} room types
                  </span>

                  {datePending > 0 && (
                    <span
                      style={{
                        fontSize: 11,
                        background: "#fff7ed",
                        color: "#c2410c",
                        border: "1px solid #fdba74",
                        borderRadius: 999,
                        padding: "3px 8px",
                        fontWeight: 800,
                      }}
                    >
                      {datePending} pending source decisions
                    </span>
                  )}

                  {hasHoliday && (
                    <span
                      style={{
                        fontSize: 11,
                        background: "#fffbeb",
                        color: "#b45309",
                        border: "1.5px solid #fde68a",
                        borderRadius: 6,
                        padding: "3px 10px",
                        fontWeight: 800,
                      }}
                    >
                      🎉 Holiday
                    </span>
                  )}
                  {hasEvent && (
                    <span
                      style={{
                        fontSize: 11,
                        background: "#f5f3ff",
                        color: "#6d28d9",
                        border: "1.5px solid #c4b5fd",
                        borderRadius: 6,
                        padding: "3px 10px",
                        fontWeight: 800,
                      }}
                    >
                      🎵 Event
                    </span>
                  )}
                </div>

                {/* Group Cards */}
                <div style={{ display: "grid", gap: 12 }}>
                  {dateGroups.map((group) => {
                    const groupSelected = selectedGroups.has(group.key);
                    const hasRisk = group.lowConfidenceCount > 0 || group.fallbackCount > 0;

                    return (
                      <div
                        key={group.key}
                        style={{
                          background: "#fff",
                          borderRadius: 14,
                          border: groupSelected
                            ? "1.5px solid #93c5fd"
                            : hasRisk
                            ? "1px solid #fecdd3"
                            : "1px solid #e2e8f0",
                          boxShadow: groupSelected
                            ? "0 4px 14px rgba(37,99,235,0.10)"
                            : "0 2px 8px rgba(0,0,0,0.04)",
                          overflow: "hidden",
                        }}
                      >
                        {/* Group Header */}
                        <div
                          style={{
                            padding: "12px 14px",
                            borderBottom: "1px solid #eef2f7",
                            background: groupSelected ? "#f8fbff" : "#fcfdff",
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            flexWrap: "wrap",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={groupSelected}
                            onChange={() => toggleGroupSelection(group.key)}
                          />

                          <div style={{ minWidth: 0, flex: "1 1 240px" }}>
                            <div
                              style={{
                                fontSize: 14,
                                fontWeight: 800,
                                color: "#0f172a",
                                letterSpacing: -0.2,
                              }}
                            >
                              {group.type_name}
                            </div>
                            <div
                              style={{
                                marginTop: 4,
                                fontSize: 12,
                                color: "#64748b",
                                display: "flex",
                                gap: 8,
                                flexWrap: "wrap",
                              }}
                            >
                              <span>{group.rows.length} source(s)</span>
                              <span>•</span>
                              <span>AI range {fmt(group.minAiPrice)} – {fmt(group.maxAiPrice)}</span>
                              <span>•</span>
                              <span>Avg confidence {(group.avgConfidence * 100).toFixed(0)}%</span>
                              {group.groupPotentialAmount > 0 && (
                                <>
                                  <span>•</span>
                                  <span style={{ color: "#1d4ed8", fontWeight: 700 }}>
                                    Potential +{fmt(group.groupPotentialAmount)}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>

                          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                            {group.pendingCount > 0 && (
                              <span
                                style={{
                                  fontSize: 11,
                                  background: "#fff7ed",
                                  color: "#c2410c",
                                  border: "1px solid #fdba74",
                                  borderRadius: 999,
                                  padding: "3px 8px",
                                  fontWeight: 800,
                                }}
                              >
                                {group.pendingCount} pending
                              </span>
                            )}
                            {group.lowConfidenceCount > 0 && (
                              <span
                                style={{
                                  fontSize: 11,
                                  background: "#fff1f2",
                                  color: "#be123c",
                                  border: "1px solid #fecdd3",
                                  borderRadius: 999,
                                  padding: "3px 8px",
                                  fontWeight: 800,
                                }}
                              >
                                {group.lowConfidenceCount} low confidence
                              </span>
                            )}
                            {group.fallbackCount > 0 && (
                              <span
                                style={{
                                  fontSize: 11,
                                  background: "#f8fafc",
                                  color: "#475569",
                                  border: "1px solid #cbd5e1",
                                  borderRadius: 999,
                                  padding: "3px 8px",
                                  fontWeight: 800,
                                }}
                              >
                                {group.fallbackCount} fallback
                              </span>
                            )}

                            <button
                              type="button"
                              onClick={() => handleApproveGroup(group)}
                              disabled={group.pendingCount === 0}
                              style={{
                                border: "1px solid #bbf7d0",
                                background: group.pendingCount ? "#f0fdf4" : "#f8fafc",
                                color: group.pendingCount ? "#166534" : "#94a3b8",
                                borderRadius: 8,
                                padding: "6px 10px",
                                fontSize: 12,
                                fontWeight: 800,
                                cursor: group.pendingCount ? "pointer" : "not-allowed",
                              }}
                            >
                              Approve all sources
                            </button>

                            <button
                              type="button"
                              onClick={() => handleRejectGroup(group)}
                              disabled={group.pendingCount === 0}
                              style={{
                                border: "1px solid #fecdd3",
                                background: group.pendingCount ? "#fff1f2" : "#f8fafc",
                                color: group.pendingCount ? "#be123c" : "#94a3b8",
                                borderRadius: 8,
                                padding: "6px 10px",
                                fontSize: 12,
                                fontWeight: 800,
                                cursor: group.pendingCount ? "pointer" : "not-allowed",
                              }}
                            >
                              Reject all pending
                            </button>
                          </div>
                        </div>

                        {/* Source rows */}
                        <div style={{ padding: "6px 10px 10px" }}>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "86px 88px 98px 98px 82px 94px 112px 140px 1fr",
                              gap: 8,
                              padding: "8px 8px",
                              marginBottom: 4,
                              color: "#64748b",
                              fontSize: 11,
                              fontWeight: 800,
                              textTransform: "uppercase",
                              letterSpacing: 0.7,
                            }}
                          >
                            <span>Source</span>
                            <span>Status</span>
                            <span>Base</span>
                            <span>AI / Final</span>
                            <span>Comp</span>
                            <span>Uplift</span>
                            <span>Confidence</span>
                            <span>AI Reasoning</span>
                            <span>Actions</span>
                          </div>

                          {group.rows.map((rec, idx) => {
                            const isSelected = selected?.id === rec.id;
                            const lowConfidence = num(rec.confidence_score) < 0.7;
                            const base = num(rec.base_rate_single);
                            const ai = num(rec.recommended_price);
                            const finalPrice = rec.final_price != null ? num(rec.final_price) : null;
                            const displayPrice = finalPrice ?? ai;

                            return (
                              <div
                                key={rec.id}
                                onClick={() => setSel(isSelected ? null : rec)}
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "86px 88px 98px 98px 82px 94px 112px 140px 1fr",
                                  gap: 8,
                                  alignItems: "center",
                                  padding: "10px 8px",
                                  borderRadius: 10,
                                  marginBottom: idx < group.rows.length - 1 ? 4 : 0,
                                  border: isSelected ? "1px solid #bfdbfe" : "1px solid #eef2f7",
                                  background: isSelected ? "#f8fbff" : "#fff",
                                  cursor: "pointer",
                                }}
                              >
                                <div>
                                  <SourceBadge source={rec.source_channel} />
                                </div>

                                <div>
                                  <StatusBadge status={rec.status} />
                                  {(rec.rate_fallback || lowConfidence) && (
                                    <div style={{ marginTop: 4, display: "flex", gap: 4, flexWrap: "wrap" }}>
                                      {rec.rate_fallback && <FallbackBadge />}
                                      {lowConfidence && (
                                        <span
                                          style={{
                                            fontSize: 10,
                                            fontWeight: 800,
                                            color: "#be123c",
                                            background: "#fff1f2",
                                            border: "1px solid #fecdd3",
                                            borderRadius: 999,
                                            padding: "2px 6px",
                                          }}
                                        >
                                          Low conf
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>

                                <div
                                  style={{
                                    fontFamily: "'DM Mono', monospace",
                                    fontSize: 12,
                                    color: "#64748b",
                                    fontWeight: 600,
                                  }}
                                >
                                  {fmt(base)}
                                </div>

                                <div style={{ minWidth: 0 }}>
                                  <div
                                    style={{
                                      fontFamily: "'DM Mono', monospace",
                                      fontSize: 12,
                                      color: "#d97706",
                                      fontWeight: 800,
                                    }}
                                  >
                                    {fmt(ai)}
                                  </div>
                                  {finalPrice != null && finalPrice !== ai && (
                                    <div
                                      style={{
                                        fontFamily: "'DM Mono', monospace",
                                        fontSize: 11,
                                        color: "#2563eb",
                                        fontWeight: 700,
                                        marginTop: 2,
                                      }}
                                    >
                                      Final {fmt(displayPrice)}
                                    </div>
                                  )}
                                </div>

                                <div
                                  style={{
                                    fontFamily: "'DM Mono', monospace",
                                    fontSize: 12,
                                    color: "#64748b",
                                    fontWeight: 600,
                                  }}
                                >
                                  {num(rec.competitor_avg_price) ? fmt(rec.competitor_avg_price) : "—"}
                                </div>

                                <div>
                                  <UpliftBadge base={rec.base_rate_single} rec={rec.recommended_price} />
                                </div>

                                <div>
                                  <ConfidenceBadge score={rec.confidence_score} />
                                </div>

                                <div onClick={(e) => e.stopPropagation()}>
                                  <ReasoningCell reasoning={rec.reasoning} />
                                </div>

                                <div onClick={(e) => e.stopPropagation()} style={{ minWidth: 0 }}>
                                  <ActionButtons
                                    rec={rec}
                                    onApprove={handleApprove}
                                    onReject={handleReject}
                                    onModify={handleModify}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {grouped.length === 0 && (
            <div
              style={{
                background: "#fff",
                borderRadius: 14,
                border: "1px solid #e2e8f0",
                textAlign: "center",
                padding: "56px 20px",
                color: "#94a3b8",
              }}
            >
              <div style={{ fontSize: 30, marginBottom: 10 }}>🔎</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: "#334155", marginBottom: 6 }}>
                No recommendations found
              </div>
              <div style={{ fontSize: 13, marginBottom: 14 }}>
                Try resetting filters or broadening your search.
              </div>
              <button
                onClick={resetFilters}
                style={{
                  border: "1px solid #e2e8f0",
                  background: "#fff",
                  color: "#334155",
                  borderRadius: 8,
                  padding: "8px 12px",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Reset filters
              </button>
            </div>
          )}
        </div>

        {/* Detail panel still source-level */}
        {selected && (
          <DetailPanel
            rec={selected}
            onClose={() => setSel(null)}
            onApprove={handleApprove}
            onReject={handleReject}
          />
        )}
      </div>

      {/* Approve all modal */}
      <ConfirmModal
        show={confirmAll}
        title="Approve All Pending?"
        message={`This will review and approve <strong>${pending} pending recommendations</strong> across all channels. Guardrails will be shown before final confirmation.`}
        confirmLabel="Continue to Guardrails"
        confirmClass="dp-btn dp-btn-success"
        onConfirm={() => {
          setCA(false);
          openBulkApproveGuardrails("BULK_ALL_PENDING");
        }}
        onCancel={() => setCA(false)}
      />

      {/* Guardrails modal */}
      <ConfirmModal
        show={guardrailModal.open}
        title="Bulk Approval Guardrails"
        message={
          guardrailModal.summary
            ? `
              You are about to approve <strong>${guardrailModal.summary.total}</strong> recommendation(s).<br/><br/>
              <strong>Risk checks:</strong><br/>
              • Low confidence: <strong>${guardrailModal.summary.lowConfidence}</strong><br/>
              • Uplift &gt; 20%: <strong>${guardrailModal.summary.highUplift}</strong><br/>
              • Above competitor by &gt; 15%: <strong>${guardrailModal.summary.compTooHigh}</strong><br/>
              • Fallback pricing used: <strong>${guardrailModal.summary.fallback}</strong><br/><br/>
              Please confirm only if this matches your revenue strategy for the selected dates/channels.
            `
            : "Reviewing guardrails..."
        }
        confirmLabel="Approve Now"
        confirmClass="dp-btn dp-btn-success"
        onConfirm={runGuardedBulkApprove}
        onCancel={() => setGuardrailModal({ open: false, mode: null, candidateIds: [], summary: null })}
      />
    </div>
  );
}